/**
 * Glue for the "tu-figurita" funnel step (2D-only flow): a single large
 * product-style photo with a discrete ↻ button to switch between the
 * front and side views. No 3D, no model-viewer, no GLB, no canvas.
 *
 * Reads the SAME shared funnel state (funnel-state.js) already written by
 * generando-preview/elegir-estilo — { petName, generationStatus,
 * selectedStyle, generationResult: { realisticUrl, animated3dUrl,
 * frontViewUrl, sideViewUrl },
 * generationJobId, sideTaskId, sideStatus } — and only displays it. The
 * main image is the URL for whichever style the customer picked on
 * /pages/elegir-estilo (selectedStyle) — never the realistic master by
 * default, unless that IS what they picked.
 *
 * This script NEVER presigns, uploads or starts a generation. The one
 * exception: if the chosen style's image is ready but the side view was
 * still processing when this page loaded, it polls the SAME read-only
 * status endpoint generation-engine.js uses (GET /api/tripo/status?
 * task_id=...) just to learn when it becomes available, so the ↻ button
 * can be enabled without a page reload. This never calls /generate again
 * and never spends credits.
 *
 * IMPORTANT: the real backend fields for this stage are side_task_id/
 * side_status/side_view_url/side_error (plus front_view_url at the top
 * level) — NOT "multiview_*". That was a wrong guess made before a real
 * response was ever inspected, and it silently broke the ↻ button: the
 * poll below kept reading a field that never existed, so it could never
 * detect a real, already-finished side view.
 */
(() => {
  const root = document.querySelector('[data-figurine-showcase-page]');
  if (!root) return;

  const config = window.PfGenerationConfig || { API_BASE: '', DEBUG: false };

  function log(...args) {
    if (config.DEBUG) console.info('[PRENDFORM]', ...args);
  }

  // Test mode (?test=1): renders with a local mock figure when no real one
  // exists yet, so the page can be reviewed without running the funnel or
  // spending Kie.ai credits. No sessionStorage write, no network call.
  const isTestMode = new URLSearchParams(window.location.search).get('test') === '1';

  const state = window.PfFunnel ? window.PfFunnel.get() : {};

  const contentEl = root.querySelector('[data-figurine-showcase-content]');
  const errorEl = root.querySelector('[data-figurine-showcase-error]');
  const imageFront = root.querySelector('[data-figurine-showcase-image]');
  const imageSide = root.querySelector('[data-figurine-showcase-image-incoming]');
  const rotateBtn = root.querySelector('[data-figurine-showcase-rotate]');
  const continueBtn = root.querySelector('[data-figurine-showcase-continue]');

  const result = state.generationStatus === 'completed' ? state.generationResult : null;

  // Which of the 2 style images is "the" image for this customer's choice.
  const STYLE_FIELD_MAP = {
    realistic: 'realisticUrl',
    animated_3d: 'animated3dUrl',
  };
  const selectedStyle = state.selectedStyle;
  const selectedStyleField = STYLE_FIELD_MAP[selectedStyle];
  const selectedStyleUrl = result && selectedStyleField && result[selectedStyleField];

  // Fallback chain for older/incomplete sessions: the chosen style's own
  // image first, then whatever the realistic master was, in that order —
  // never silently show a DIFFERENT style than the one actually picked
  // when selectedStyleUrl exists.
  const realFigureImageUrl = selectedStyleUrl || (result && (result.realisticUrl || result.figureImageUrl));
  const usingMock = !realFigureImageUrl && isTestMode;
  const figureImageUrl = realFigureImageUrl || (usingMock ? window.PfMockFigureUrl : null);
  const petName = usingMock ? 'Milo' : state.petName || 'ta mascotte';

  root.querySelectorAll('[data-figurine-showcase-pet-name]').forEach((el) => {
    el.textContent = el.textContent.replace('[PET_NAME]', petName);
  });

  continueBtn?.addEventListener('click', () => {
    const nextUrl = continueBtn.dataset.nextUrl || '/pages/elegir-peana';
    window.location.href = usingMock ? nextUrl + '?test=1' : nextUrl;
  });

  function showError() {
    if (contentEl) contentEl.hidden = true;
    if (errorEl) errorEl.hidden = false;
  }

  // Nothing to show at all — genuinely not found.
  if (!figureImageUrl) {
    showError();
    return;
  }

  if (contentEl) contentEl.hidden = false;
  if (errorEl) errorEl.hidden = true;

  // The front/side images are loaded once into two fixed <img> elements;
  // toggling the ↻ button only ever flips which one is visible (a CSS
  // opacity crossfade) — no new fetch, no re-assigning src per click.
  let showingFront = true;
  let sideReady = false;

  function setFrontSrc(url) {
    if (imageFront) {
      imageFront.src = url;
      imageFront.hidden = false;
      imageFront.classList.add('is-visible');
    }
  }

  function enableSideView(frontUrl, sideUrl) {
    setFrontSrc(frontUrl);
    if (imageSide) {
      imageSide.src = sideUrl;
      imageSide.hidden = false;
    }
    sideReady = true;
    if (rotateBtn) {
      rotateBtn.disabled = false;
      rotateBtn.classList.remove('is-preparing');
      rotateBtn.setAttribute('aria-label', rotateBtn.dataset.readyLabel || 'Changer de vue');
    }
  }

  // While the side view is still generating, show a subtle spin on the ↻
  // icon instead of a plain dead disabled button — same disabled state,
  // just communicates "this is coming" rather than "broken". Removed by
  // enableSideView() the moment it's actually ready.
  rotateBtn?.classList.add('is-preparing');

  // Show whatever is available right now, immediately — never wait on the
  // side view to show the customer their figurine.
  setFrontSrc((result && result.frontViewUrl) || figureImageUrl);

  // Sessions saved before the multiview_* -> side_* field-name fix only
  // have the old key names — fall back to those so an existing customer's
  // in-progress session (waiting on the side view right now) recovers
  // without having to restart the funnel.
  const sideStatus = state.sideStatus || state.multiviewStatus;
  const sideTaskId = state.sideTaskId || state.multiviewTaskId;

  // Every /api/tripo/status call involved in the side view (trigger AND
  // every follow-up poll) must carry all three: task_id is always the
  // MASTER task (required by the backend contract), side_task_id is the
  // side/profile task to check on (never sent as task_id), and
  // selected_style is what lets the backend resolve which style's image
  // the side view is chained off. masterTaskId is needed by both the
  // "already have a sideTaskId" path below and the self-heal path further
  // down, so it's computed once here.
  const masterTaskId = state.masterTaskId || state.generationJobId;

  // In test mode, the ↻ button toggles between the same mock image on
  // both sides — enough to verify the wiring without a real side view.
  const sideReadyPerState =
    usingMock ||
    (!!(result && result.frontViewUrl && result.sideViewUrl) && (sideStatus ? sideStatus === 'success' : true));
  const sideFailed = sideStatus === 'failed' || sideStatus === 'cancelled';

  if (sideReadyPerState) {
    enableSideView((result && result.frontViewUrl) || figureImageUrl, (result && result.sideViewUrl) || figureImageUrl);
  }

  rotateBtn?.addEventListener('click', () => {
    if (!sideReady) return;
    showingFront = !showingFront;
    imageFront.classList.toggle('is-visible', showingFront);
    imageSide.classList.toggle('is-visible', !showingFront);
  });

  if (sideReadyPerState) {
    return;
  }

  if (sideFailed) {
    // The backend itself reported the side view failed — nothing to poll
    // or retry, stop spinning and leave the button quietly disabled.
    rotateBtn?.classList.remove('is-preparing');
    return;
  }

  // Side view not ready yet: keep the figure image fully usable right now,
  // and poll (read-only, no credits) in the background until it is. Single
  // controller, same backoff pattern as generation-engine.js.
  const POLL_INTERVAL_MS = 2500;
  const POLL_RETRY_BACKOFF_MS = [2000, 3000, 5000];
  let pollTimer = null;
  let pollFailureCount = 0;

  function stopPolling() {
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
  }

  // Builds the poll URL per the backend's real contract: task_id is ALWAYS
  // the master task, side_task_id is the side task being followed (never
  // reused as task_id — that was the bug: it made the backend treat the
  // side task as if it were a brand new master generation, so it could
  // never see side_status: "success" no matter how long it polled), and
  // selected_style is sent on every call, not just the first one.
  function sideStatusUrl(taskIdToPoll) {
    const params = new URLSearchParams({ task_id: masterTaskId, side_task_id: taskIdToPoll });
    if (selectedStyle) params.set('selected_style', selectedStyle);
    return `${config.API_BASE}/api/tripo/status?${params.toString()}`;
  }

  async function pollSideViewStatus(taskIdToPoll) {
    try {
      const res = await fetch(sideStatusUrl(taskIdToPoll));
      if (!res.ok) throw new Error('status_failed');
      const data = await res.json();
      pollFailureCount = 0;

      if (data.side_status === 'success' && data.side_view_url) {
        stopPolling();
        window.PfFunnel?.set({
          sideStatus: 'success',
          generationResult: Object.assign({}, window.PfFunnel.get().generationResult, {
            frontViewUrl: data.front_view_url,
            sideViewUrl: data.side_view_url,
          }),
        });
        log('Side view ready:', data.front_view_url, data.side_view_url);
        enableSideView(data.front_view_url, data.side_view_url);
        return;
      }

      if (data.side_status === 'failed' || data.side_status === 'cancelled') {
        stopPolling();
        rotateBtn?.classList.remove('is-preparing');
        window.PfFunnel?.set({ sideStatus: data.side_status });
        return;
      }

      window.PfFunnel?.set({
        sideStatus: data.side_status === 'queued' ? 'queued' : 'processing',
      });
      pollTimer = setTimeout(() => pollSideViewStatus(taskIdToPoll), POLL_INTERVAL_MS);
    } catch (e) {
      pollFailureCount += 1;
      if (pollFailureCount > POLL_RETRY_BACKOFF_MS.length) {
        stopPolling();
        rotateBtn?.classList.remove('is-preparing');
        return;
      }
      pollTimer = setTimeout(() => pollSideViewStatus(taskIdToPoll), POLL_RETRY_BACKOFF_MS[pollFailureCount - 1]);
    }
  }

  if (sideTaskId && masterTaskId) {
    pollSideViewStatus(sideTaskId);
    return;
  }

  if (sideTaskId && !masterTaskId) {
    // Can't form a valid /api/tripo/status call without the master task_id
    // (the backend requires it) — an unrecoverable legacy state, not
    // something to retry into.
    rotateBtn?.classList.remove('is-preparing');
    return;
  }

  // No sideTaskId at all: normally elegir-estilo's own "Continuer" click
  // already triggered the side view (see style-picker.js) and left it in
  // state. If that one-shot, fire-and-forget trigger call failed silently
  // (a transient network error / cold start / non-OK response — it has no
  // retry of its own) or this session reached this page without ever going
  // through that click, the button was previously left permanently
  // disabled with no recovery path at all. Self-heal by retrying the SAME
  // read-only trigger once, with the SAME contract, instead of inventing
  // any new resource or endpoint.
  if (!masterTaskId || !selectedStyle) {
    // Genuinely nothing to retry or poll with yet — leave it quietly
    // disabled rather than spin forever.
    rotateBtn?.classList.remove('is-preparing');
    return;
  }

  fetch(`${config.API_BASE}/api/tripo/status?task_id=${encodeURIComponent(masterTaskId)}&selected_style=${encodeURIComponent(selectedStyle)}`)
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (!data) {
        rotateBtn?.classList.remove('is-preparing');
        return;
      }
      const recoveredSideTaskId = data.side_task_id || masterTaskId;
      const recoveredSideStatus = data.side_status || 'queued';
      const currentResult = window.PfFunnel?.get().generationResult || {};
      window.PfFunnel?.set({
        sideTaskId: recoveredSideTaskId,
        sideStatus: recoveredSideStatus,
        generationResult: Object.assign({}, currentResult, {
          frontViewUrl: data.front_view_url || currentResult.frontViewUrl || null,
          sideViewUrl: data.side_view_url || currentResult.sideViewUrl || null,
        }),
      });
      log('Side view trigger self-heal response:', data);

      if (recoveredSideStatus === 'success' && data.side_view_url) {
        enableSideView(data.front_view_url || figureImageUrl, data.side_view_url);
        return;
      }
      if (recoveredSideStatus === 'failed' || recoveredSideStatus === 'cancelled') {
        rotateBtn?.classList.remove('is-preparing');
        return;
      }
      pollSideViewStatus(recoveredSideTaskId);
    })
    .catch(() => {
      rotateBtn?.classList.remove('is-preparing');
    });
})();
